import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from "react";
import NotificationComponent from "./NotificationComponent";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { MODULES } from "../../const";

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const [allNotifications, setAllNotifications] = useState([]);
    const [notifications, setNotifications] = useState([]); // visible top 10
    const [enabled, setEnabled] = useState(true); // default to true

    const user = useSelector((state) => state.user);
    const auth = useSelector((state) => state.user.permissions[MODULES?.Master]);
    const viewNotificationPermission = auth?.NOTIFICATION_TOGGLE?.FORM?.VIEW;

    // Load from localStorage on mount
    useEffect(() => {
        const storedValue = localStorage.getItem("notificationsEnabled");
        if (storedValue !== null) {
            setEnabled(storedValue === "true");
        }
    }, []);

    // Persist to localStorage when it changes
    useEffect(() => {
        localStorage.setItem("notificationsEnabled", enabled.toString());
    }, [enabled]);

    const refreshVisibleNotifications = useCallback(() => {
        setNotifications((prev) => {
            return allNotifications
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 10);
        });
    }, [allNotifications]);

    const addNotification = useCallback(
        (id, message, type = "success", date = new Date()) => {
            if (!viewNotificationPermission) return;
            const newNotification = {
                id: id ? id : Date.now() + Math.random(),
                message,
                type,
                date,
            };

            setAllNotifications((prev) => {
                // Avoid duplicates
                const filtered = prev.filter((n) => n.id !== id);
                const updated = [{ id, message, type, date }, ...filtered];
                return updated;
            });
        },
        [viewNotificationPermission]
    );

    useEffect(() => {
        refreshVisibleNotifications();
    }, [allNotifications, refreshVisibleNotifications]);

    const removeNotification = useCallback((id) => {
        setAllNotifications((prev) => {
            const updated = prev.filter((n) => n.id !== id);
            return updated;
        });
    }, []);

    const toggleNotifications = useCallback((value) => {
        if (!viewNotificationPermission) return;
        setEnabled(value);
    }, [viewNotificationPermission]);

    return (
        <NotificationContext.Provider
            value={{ addNotification, toggleNotifications, enabled: enabled && !!viewNotificationPermission }}
        >
            {children}
            {viewNotificationPermission && enabled &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: 80,
                            right: 20,
                            zIndex: 9999,
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                            maxHeight: "calc(100vh - 100px)",
                            overflowY: "auto",
                            background: "transparent",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {notifications.map((notification) => (
                            <NotificationComponent
                                key={notification.id}
                                open={true}
                                message={notification.message}
                                type={notification.type}
                                onClose={() => removeNotification(notification.id)}
                                id={notification.id}
                                loading={false}
                            />
                        ))}
                    </div>,
                    document.body
                )}
        </NotificationContext.Provider>
    );
};
